import { useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { CharacterClassIcon } from 'app/frontend/base/components'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import { useRaidPlanTopExecutionKills } from 'app/frontend/base/config/queries'
import {
  RaidPlanOwner,
  importRaidPlanTopExecutionKillTimeline,
} from 'app/frontend/base/api/raid_plans'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  getClass,
  getHealerSpecializations,
  getSpecialization,
} from 'app/frontend/base/services/characterValues'
import { CharacterClass } from 'app/frontend/types/custom'
import RaidPlan from 'app/frontend/types/custom/RaidPlan'
import Card from 'app/frontend/base/components/Card'
import LinkButton from '@/components/ui/link-button'
import { imageUrl } from 'app/frontend/base/helpers'
import { cn } from '@/lib/utils'
import {
  formatFightDuration,
  getTopExecutionKillTimeline,
} from './warcraftLogsImport'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  encounterId: number
  planId: number
  owner: RaidPlanOwner
  onImported: (raidPlan: RaidPlan) => void
}

type TimelineActorSummary = {
  key: string
  classId: number
  specId: number | null
  className?: string
  specName?: string
  specIcon?: string
}

const summarizeActors = (kill: wowaudit.RaidReportTopExecutionKill) => {
  const healers: TimelineActorSummary[] = []
  const others: TimelineActorSummary[] = []
  const timeline = getTopExecutionKillTimeline(kill)

  timeline.actors.forEach((actor, actorIndex) => {
    const spec = actor.spec_id
      ? getSpecialization(actor.spec_id.toString())
      : undefined
    const className = getClass(actor.class_id.toString())
    const summary = {
      key: `${actor.uuid}-${actorIndex}`,
      classId: actor.class_id,
      specId: actor.spec_id,
      className,
      specName: spec?.name,
      specIcon: spec?.icon,
    }
    if (spec?.default_role === 'heal') {
      healers.push(summary)
    } else {
      others.push(summary)
    }
  })

  return { healers, others }
}

const sortActorSummaries = (actors: TimelineActorSummary[]) => {
  return [...actors].sort((left, right) => {
    if (left.classId !== right.classId) return left.classId - right.classId
    const leftSpecId = left.specId ?? 0
    const rightSpecId = right.specId ?? 0
    return leftSpecId - rightSpecId
  })
}

const ActorIcons = ({ actors }: { actors: TimelineActorSummary[] }) => {
  if (!actors.length) {
    return <span className='text-xs text-muted-foreground'>-</span>
  }

  const sortedActors = sortActorSummaries(actors)

  return (
    <div className='flex items-center gap-1 flex-wrap'>
      {sortedActors.map((actor) =>
        actor.specIcon ? (
          <BlizzardIcon
            key={actor.key}
            iconName={actor.specIcon}
            size='medium'
            className='rounded-sm'
          />
        ) : actor.className ? (
          <CharacterClassIcon
            key={actor.key}
            characterClass={actor.className as CharacterClass}
            size='medium'
          />
        ) : null,
      )}
    </div>
  )
}

const ImportFromWarcraftLogsDialog = ({
  open,
  onOpenChange,
  encounterId,
  planId,
  owner,
  onImported,
}: Props) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [hiddenHealerSpecIds, setHiddenHealerSpecIds] = useState<
    Record<number, true>
  >({})
  const { data: kills = [], isLoading } = useRaidPlanTopExecutionKills(
    owner,
    encounterId,
    open,
  )

  const importMutation = useMutation({
    mutationFn: async (topExecutionKillId: number) =>
      importRaidPlanTopExecutionKillTimeline(owner, planId, topExecutionKillId),
    onSuccess: async (updatedPlan) => {
      await queryClient.setQueryData(
        ['raidPlan', { owner, planId: updatedPlan.id }],
        updatedPlan,
      )
      await queryClient.invalidateQueries({ queryKey: ['raidPlans'] })
      await queryClient.invalidateQueries({ queryKey: ['userRaidPlans'] })
      onImported(updatedPlan)
      onOpenChange(false)
    },
  })

  const rows = useMemo(
    () =>
      kills.map((kill) => {
        const actorSummary = summarizeActors(kill)
        const timeline = getTopExecutionKillTimeline(kill)
        const totalCasts = timeline.actors.reduce(
          (count, actor) => count + actor.casts.length,
          0,
        )

        return {
          kill,
          actorSummary,
          healerSpecIds: actorSummary.healers
            .map((actor) => actor.specId)
            .filter((specId): specId is number => specId !== null),
          totalCasts,
        }
      }),
    [kills],
  )
  const healerSpecs = getHealerSpecializations()
  const healerUsagePercentBySpecId = useMemo(() => {
    const totals: Record<number, number> = {}
    rows.forEach((row) => {
      const uniqueSpecIds = Array.from(new Set(row.healerSpecIds))
      uniqueSpecIds.forEach((specId) => {
        totals[specId] = (totals[specId] ?? 0) + 1
      })
    })
    const denominator = rows.length
    if (denominator === 0) return {}
    return Object.entries(totals).reduce<Record<number, number>>(
      (acc, [specId, count]) => {
        acc[Number(specId)] = Math.round((count / denominator) * 100)
        return acc
      },
      {},
    )
  }, [rows])
  const visibleRows = useMemo(() => {
    return rows.filter((row) =>
      row.healerSpecIds.every((specId) => !hiddenHealerSpecIds[specId]),
    )
  }, [hiddenHealerSpecIds, rows])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-4xl'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <img src={imageUrl('wcl-logo.png')} className='w-6 h-6' />
            {t('dashboard.team.plans.onboarding.import-from-warcraft-logs')}
          </DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <div className='flex items-center justify-center py-8'>
            <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />
          </div>
        ) : rows.length === 0 ? (
          <div className='text-sm text-muted-foreground py-4'>
            {t('dashboard.team.plans.onboarding.wcl.empty')}
          </div>
        ) : (
          <>
            <div className='flex justify-between items-end gap-8'>
              <p>
                These are the top {rows.length} kills for this encounter (by
                guild execution). You can import all key cooldowns that were
                cast on these kills. This will replace your existing timeline!
              </p>

              {healerSpecs.length > 0 ? (
                <div className='flex flex-col items-end gap-1'>
                  <div className='text-sm font-medium'>Filter specs</div>
                  <div className='flex items-center'>
                    {healerSpecs.map((healerSpec) => {
                      const isActive = !hiddenHealerSpecIds[healerSpec.id]
                      return (
                        <button
                          key={healerSpec.id}
                          type='button'
                          className='inline-flex flex-col items-center justify-center shrink-0 min-w-10'
                          onClick={() =>
                            setHiddenHealerSpecIds((current) => {
                              const next = { ...current }
                              if (next[healerSpec.id]) {
                                delete next[healerSpec.id]
                              } else {
                                next[healerSpec.id] = true
                              }
                              return next
                            })
                          }
                        >
                          <BlizzardIcon
                            iconName={healerSpec.icon}
                            size='large'
                            className={cn(
                              'w-8 h-8 min-w-8 min-h-8',
                              isActive ? '' : 'opacity-20 grayscale',
                            )}
                          />
                          <div className='text-xs leading-none text-muted-foreground mt-1'>
                            {healerUsagePercentBySpecId[healerSpec.id] ?? 0}%
                          </div>
                        </button>
                      )
                    })}
                  </div>
                </div>
              ) : null}
            </div>

            <Card className='max-h-[70vh] overflow-auto'>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      {t('dashboard.team.plans.onboarding.wcl.rank')}
                    </TableHead>
                    <TableHead>
                      {t('dashboard.team.plans.onboarding.wcl.duration')}
                    </TableHead>
                    <TableHead>
                      {t('dashboard.team.plans.onboarding.wcl.healers')}
                    </TableHead>
                    <TableHead>
                      {t('dashboard.team.plans.onboarding.wcl.other-actors')}
                    </TableHead>
                    <TableHead>
                      {t('dashboard.team.plans.onboarding.wcl.casts')}
                    </TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleRows.map(({ kill, actorSummary, totalCasts }) => (
                    <TableRow key={kill.id}>
                      <TableCell className='font-semibold'>
                        #{kill.rank}
                      </TableCell>
                      <TableCell>
                        {formatFightDuration(kill.fightDurationMs)}
                      </TableCell>
                      <TableCell>
                        <ActorIcons actors={actorSummary.healers} />
                      </TableCell>
                      <TableCell>
                        <ActorIcons actors={actorSummary.others} />
                      </TableCell>
                      <TableCell>{totalCasts}</TableCell>
                      <TableCell className='text-right'>
                        <div className='flex items-center justify-end gap-2'>
                          <LinkButton
                            size='xs'
                            target='_blank'
                            rel='noreferrer'
                            to={`https://www.warcraftlogs.com/reports/${kill.reportCode}#fight=${kill.fightId}`}
                          >
                            View
                          </LinkButton>
                          <Button
                            size='xs'
                            onClick={() => importMutation.mutate(kill.id)}
                            disabled={importMutation.isPending}
                          >
                            {t('dashboard.team.plans.onboarding.wcl.import')}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default ImportFromWarcraftLogsDialog
